import { useEffect, useRef, useState } from 'react';
import init, { RailwayScene } from './wasm/railway_core';
import wasmUrl from './wasm/railway_core_bg.wasm?url';

const ENABLE_DITHER = true;
const RENDER_BUFFER_STOPS = true;
const PICKET_STRIDE = 10;
const DEBUG = false;
// Shared with railway-wasm/src/lib.rs: metadata, then 18-float track headers.
const META = 16;
const HEADER = 18;
let wasmReady: ReturnType<typeof init> | undefined;

export type RailwayCanvasProps = {
    className?: string;
    speed?: number;
    density?: number;
    interactive?: boolean;
    paused?: boolean;
    dark?: boolean;
};

/** Fill a sized parent. Math and projection are batched in WASM; canvas only draws. */
export function RailwayCanvas({ className, speed = 1, density = 1, interactive = true, paused = false, dark = false }: RailwayCanvasProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const settings = useRef({ speed, density, interactive, paused, dark });
    const [failed, setFailed] = useState(false);

    useEffect(() => { settings.current = { speed, density, interactive, paused, dark }; }, [speed, density, interactive, paused, dark]);

    useEffect(() => {
        const canvas = canvasRef.current!;
        const context = canvas.getContext('2d', { alpha: false });
        if (!context) { setFailed(true); return; }
        const ctx = context;
        let disposed = false;
        let frame = 0;
        let scene: RailwayScene | undefined;
        let memory: WebAssembly.Memory;
        let points: Float32Array;
        let pickets: Float32Array;
        let debugMap: Float32Array;
        let width = 1, height = 1, dpr = 1;
        let previous = 0, impulse = 0;
        let targetX = 0, targetY = 0, pointerX = 0, pointerY = 0;
        let dirty = true;
        let inView = true;
        let pointerBounds: DOMRect | undefined;
        let previousDark = settings.current.dark;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        let reduced = reducedMotion.matches;

        // A fixed ordered pattern: no random values, temporal noise, or flicker.
        const tile = document.createElement('canvas');
        tile.width = tile.height = 8;
        const tileContext = tile.getContext('2d');
        if (tileContext) {
            tileContext.fillStyle = 'rgba(27, 28, 24, 0.035)';
            tileContext.fillRect(0, 0, 1, 1);
            tileContext.fillRect(4, 4, 1, 1);
        }
        const grain = ctx.createPattern(tile, 'repeat');

        function resize() {
            pointerBounds = undefined;
            dpr = Math.min(window.devicePixelRatio || 1, 2);
            canvas.width = Math.round(width * dpr);
            canvas.height = Math.round(height * dpr);
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            dirty = true;
        }
        const observer = new ResizeObserver(([entry]) => {
            width = Math.max(1, entry.contentRect.width);
            height = Math.max(1, entry.contentRect.height);
            resize();
        });
        observer.observe(canvas.parentElement ?? canvas);

        function move(event: PointerEvent) {
            if (event.pointerType !== 'mouse' || !settings.current.interactive) return;
            const rect = pointerBounds ??= canvas.getBoundingClientRect();
            targetX = Math.max(-1, Math.min(1, (event.clientX - rect.left) / width * 2 - 1));
            targetY = Math.max(-1, Math.min(1, (event.clientY - rect.top) / height * 2 - 1));
        }
        function leave() { targetX = targetY = 0; pointerBounds = undefined; }
        function scroll() { pointerBounds = undefined; }
        function wheel(event: WheelEvent) {
            if (settings.current.interactive && !reduced && !settings.current.paused)
                impulse = Math.min(2, impulse + Math.abs(event.deltaY) * 0.001);
        }
        function motionChange() { reduced = reducedMotion.matches; dirty = true; previous = 0; }
        function visibilityChange() {
            previous = 0;
            cancelAnimationFrame(frame);
            if (!document.hidden && inView && scene) frame = requestAnimationFrame(draw);
        }
        const host = canvas.parentElement ?? canvas;
        const intersection = new IntersectionObserver(([entry]) => {
            inView = entry.isIntersecting;
            visibilityChange();
        });
        intersection.observe(host);
        host.addEventListener('pointermove', move);
        host.addEventListener('pointerleave', leave);
        host.addEventListener('wheel', wheel, { passive: true });
        reducedMotion.addEventListener('change', motionChange);
        document.addEventListener('visibilitychange', visibilityChange);
        window.addEventListener('resize', resize);
        window.addEventListener('scroll', scroll, { passive: true, capture: true });

        function path(start: number, count: number, connected: boolean) {
            let lastX = Infinity, lastY = Infinity;
            for (let i = start, end = start + count * 4; i < end; i += 4) {
                if (!connected || points[i] !== lastX || points[i + 1] !== lastY)
                    ctx.moveTo(points[i], points[i + 1]);
                ctx.lineTo(points[i + 2], points[i + 3]);
                lastX = points[i + 2]; lastY = points[i + 3];
            }
        }

        function draw(now: number) {
            if (disposed || !scene || document.hidden || !inView) return;
            const dt = previous ? Math.min((now - previous) / 1000, 0.05) : 0;
            previous = now;
            const options = settings.current;
            const still = reduced || options.paused;
            if (previousDark !== options.dark) { dirty = true; previousDark = options.dark; }
            if (dpr !== Math.min(window.devicePixelRatio || 1, 2)) resize();
            if (!still || dirty) {
                const step = still ? 0 : dt;
                const inertia = 1 - Math.exp(-step * 2.4);
                pointerX += ((options.interactive ? targetX : 0) - pointerX) * inertia;
                pointerY += ((options.interactive ? targetY : 0) - pointerY) * inertia;
                impulse *= Math.exp(-step * 2);
                scene.update_scene(width, height, step, pointerX, pointerY,
                    (Number.isFinite(options.speed) ? options.speed : 1) + impulse,
                    Number.isFinite(options.density) ? options.density : 1);
                if (!points || points.buffer !== memory.buffer) {
                    points = new Float32Array(memory.buffer, scene.points_ptr(), scene.points_len());
                    pickets = new Float32Array(memory.buffer, scene.pickets_ptr(), scene.pickets_len());
                    if (DEBUG) debugMap = new Float32Array(memory.buffer, scene.debug_map_ptr(), scene.debug_map_len());
                }

                ctx.fillStyle = options.dark ? '#1d1f1b' : '#eeede6';
                ctx.fillRect(0, 0, width, height);
                ctx.strokeStyle = options.dark ? '#eeede6' : '#22241f';
                const tracks = points[0];
                // Four batches regardless of track count. Each header owns its sleepers
                // and two continuous rails; secondary routes use finer strokes.
                for (let group = 0; group < 2; group++) {
                    ctx.beginPath();
                    ctx.lineWidth = group === 0 ? 0.9 : 0.7;
                    for (let track = group === 0 ? 0 : 1; track < (group === 0 ? 1 : tracks); track++) {
                        const h = META + track * HEADER;
                        path(points[h + 2], points[h + 3], false);
                    }
                    ctx.stroke();
                    ctx.beginPath();
                    ctx.lineWidth = group === 0 ? 1.9 : 1.1;
                    for (let track = group === 0 ? 0 : 1; track < (group === 0 ? 1 : tracks); track++) {
                        const h = META + track * HEADER;
                        path(points[h + 4], points[h + 5], true);
                        path(points[h + 6], points[h + 7], true);
                    }
                    ctx.stroke();
                }
                ctx.fillStyle = options.dark ? '#eeede6' : '#22241f';
                ctx.lineWidth = 0.85;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'bottom';
                const markerCount = scene.picket_count();
                for (let marker = 0; marker < markerCount; marker++) {
                    const i = marker * PICKET_STRIDE;
                    const alpha = pickets[i + 6];
                    if (alpha <= 0 || pickets[i] < -40 || pickets[i] > width + 40) continue;
                    ctx.globalAlpha = alpha;
                    ctx.beginPath();
                    ctx.moveTo(pickets[i], pickets[i + 1]);
                    ctx.lineTo(pickets[i + 2], pickets[i + 3]);
                    ctx.stroke();
                    const textSize = Math.min(14, pickets[i + 5] * 0.45);
                    if (textSize >= 6) {
                        const t = Math.min(1, (textSize - 6) / 3);
                        ctx.globalAlpha = alpha * t * t * (3 - 2 * t);
                        ctx.font = `${textSize}px monospace`;
                        ctx.fillText(`${pickets[i + 7]} / ${pickets[i + 8]}`, pickets[i + 2], pickets[i + 3] - 3);
                    }
                }
                ctx.globalAlpha = 1;
                ctx.textAlign = 'start';
                ctx.textBaseline = 'alphabetic';
                if (ENABLE_DITHER && grain) {
                    ctx.fillStyle = grain;
                    ctx.fillRect(0, 0, width, height);
                }
                if (DEBUG) {
                    ctx.strokeStyle = ctx.fillStyle = '#a13e28';
                    ctx.lineWidth = 1;
                    ctx.font = '12px monospace';
                    ctx.beginPath();
                    for (let track = 0; track < tracks; track++) {
                        const h = META + track * HEADER;
                        path(points[h + 8], points[h + 9], true);
                        for (let j = 10; j <= 12; j += 2) {
                            const x = points[h + j], y = points[h + j + 1];
                            if (x || y) {
                                ctx.moveTo(x - 4, y); ctx.lineTo(x + 4, y);
                                ctx.moveTo(x, y - 4); ctx.lineTo(x, y + 4);
                                ctx.fillText(`#${points[h]} ${j === 10 ? 'start' : 'end'}`, x + 6, y - 5);
                            }
                        }
                        ctx.fillText(`#${points[h]} heading=${points[h + 14].toFixed(3)} k=${points[h + 15].toFixed(5)}`, 24, 130 + track * 16);
                    }
                    path(points[12], points[13], false);
                    ctx.stroke();
                    ctx.fillText(`${Math.round(1 / Math.max(dt, 0.001))} fps / ${tracks} tracks / chunk ${points[6]}`, 24, 90);
                    ctx.fillText(`camera s=${points[1].toFixed(1)} x=${points[8].toFixed(1)} z=${points[9].toFixed(1)} heading=${points[7].toFixed(3)} k=${points[3].toFixed(5)}`, 24, 108);
                    ctx.fillRect(width / 2 - 3, height - 8, 6, 6);
                    ctx.save();
                    ctx.beginPath(); ctx.rect(width - 344, 90, 320, 250); ctx.clip();
                    ctx.fillStyle = '#eeede6'; ctx.fillRect(width - 344, 90, 320, 250);
                    const colors = ['#20241f', '#245b8a', '#bf831b', '#b53535', '#b5b6ae'];
                    const count = scene.debug_map_count();
                    for (const category of [4, 2, 3, 0, 1]) {
                        ctx.strokeStyle = colors[category];
                        ctx.lineWidth = category === 4 ? 0.6 : 1;
                        ctx.beginPath();
                        for (let i = 0; i < count * 5; i += 5) {
                            if (debugMap[i + 4] !== category) continue;
                            ctx.moveTo(debugMap[i], debugMap[i + 1]); ctx.lineTo(debugMap[i + 2], debugMap[i + 3]);
                        }
                        ctx.stroke();
                    }
                    ctx.fillStyle = '#22241f';
                    ctx.fillText(`PLAN 400m · rejected ${points[14]}`, width - 336, 106);
                    ctx.font = '9px monospace';
                    ctx.fillText('ochre: reservations / red: rejected / grey: clearance', width - 336, 121);
                    ctx.fillRect(width - 187, 327, 6, 6);
                    ctx.restore();
                }
                dirty = false;
                canvas.dataset.ready = 'true';
            }
            frame = requestAnimationFrame(draw);
        }

        wasmReady ??= init({ module_or_path: wasmUrl }).catch((error: unknown) => {
            wasmReady = undefined;
            throw error;
        });
        void wasmReady.then((wasm) => {
            if (disposed) return;
            memory = wasm.memory;
            scene = new RailwayScene();
            scene.set_debug(DEBUG);
            scene.set_buffer_stops(RENDER_BUFFER_STOPS);
            frame = requestAnimationFrame(draw);
        }).catch((error: unknown) => {
            if (!disposed) { setFailed(true); console.error('Railway WASM initialization failed', error); }
        });

        return () => {
            disposed = true;
            cancelAnimationFrame(frame);
            observer.disconnect();
            intersection.disconnect();
            host.removeEventListener('pointermove', move);
            host.removeEventListener('pointerleave', leave);
            host.removeEventListener('wheel', wheel);
            reducedMotion.removeEventListener('change', motionChange);
            document.removeEventListener('visibilitychange', visibilityChange);
            window.removeEventListener('resize', resize);
            window.removeEventListener('scroll', scroll, true);
            scene?.free();
        };
    }, []);

    return <>
        <canvas ref={canvasRef} className={className} aria-hidden="true"
            style={{ display: 'block', width: '100%', height: '100%', background: dark ? '#1d1f1b' : '#eeede6' }} />
        {failed && <p role="alert" style={{ position: 'absolute', top: '50%', left: '8%', right: '8%' }}>
            Не удалось загрузить анимацию. Обновите страницу, чтобы попробовать снова.
        </p>}
    </>;
}
